import { SerializedError, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { ApiQueries, REQUEST_METHODS } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { parseError } from '@lib/core/service/requests/parseError';
import { request } from '@lib/core/service/requests/request';
import { selectDietaryPreferenceListData } from '@lib/core/users/selectors/dietaryPreference';
import {
  dietaryPreferenceApiUrlCreator,
  dietaryPreferenceMultipleRemoveApiUrlCreator,
  dietaryPreferenceMultipleUpdateApiUrlCreator,
} from '@lib/core/users/slices/urls';
import {
  IUpdateDietaryPreferenceParams,
  IUpdateMultipleDietaryPreferenceParams,
  TDietaryPreference,
  TDietaryPreferenceOptionsResponse,
} from '@lib/core/users/types';
import { DIETARY_PREFERENCE_SLUG } from '@lib/tools/shared/helpers/consts';

export interface IDietaryPreferenceState {
  list: {
    data: TDietaryPreference[];
    error: SerializedError;
    isLoading: boolean;
  };
  options: {
    data: TDietaryPreferenceOptionsResponse;
    error: SerializedError;
    isLoading: boolean;
  };
}

export const initialState: IDietaryPreferenceState = {
  list: {
    data: [],
    error: null,
    isLoading: false,
  },
  options: {
    data: { choices: { dietary_preferences: [] } },
    error: null,
    isLoading: false,
  },
};

export const actionGetDietaryPreferenceOptionsData = createTypedAsyncThunk<TDietaryPreferenceOptionsResponse>(
  'actionGetDietaryPreferenceOptionsData',
  async () =>
    request({
      method: REQUEST_METHODS.OPTIONS,
      url: dietaryPreferenceApiUrlCreator(),
    }),
);

export const actionGetDietaryPreferenceListData = createTypedAsyncThunk<TDietaryPreference[]>(
  'actionGetDietaryPreferenceListData',
  async () => {
    const dietaryPreferenceResponse = await request({
      url: dietaryPreferenceApiUrlCreator(),
    });
    return dietaryPreferenceResponse?.results ?? [];
  },
);

export const actionUpdateDietaryPreferenceListData = createTypedAsyncThunk<
  TDietaryPreference[],
  IUpdateDietaryPreferenceParams
>('actionUpdateDietaryPreferenceListData', async ({ slugToUpdate, exposureToUpdate }, { getState }) => {
  try {
    const dietaryPreferenceListData = selectDietaryPreferenceListData(getState());

    const isNoPreferenceSlug = (slug: string) =>
      [DIETARY_PREFERENCE_SLUG.NO_FOOD_PREFERENCES, DIETARY_PREFERENCE_SLUG.NO_PREFERENCES].includes(slug);

    const removePreference = async (slug: string) =>
      await request({
        method: REQUEST_METHODS.DELETE,
        url: dietaryPreferenceApiUrlCreator(slug),
      });

    const addPreference = async (slug: string) =>
      await request({
        body: { [ApiQueries.dietaryPreference]: slug },
        method: REQUEST_METHODS.POST,
        url: dietaryPreferenceApiUrlCreator(),
      });

    const isPreferenceToUpdateInList = dietaryPreferenceListData.some(
      ({ dietary_preference: { slug } }) => slug === slugToUpdate,
    );

    if (isNoPreferenceSlug(slugToUpdate)) {
      if (isPreferenceToUpdateInList) {
        // remove no-preference preference
        await removePreference(slugToUpdate);

        return dietaryPreferenceListData.filter(({ dietary_preference: { slug } }) => slug !== slugToUpdate);
      }

      const shouldDeleteAllPreferencesOfExposure = dietaryPreferenceListData.some(
        ({ dietary_preference: { exposure } }) => exposure === exposureToUpdate,
      );

      if (shouldDeleteAllPreferencesOfExposure) {
        // remove all preferences of the exposure
        await request({
          method: REQUEST_METHODS.DELETE,
          url: dietaryPreferenceMultipleRemoveApiUrlCreator(exposureToUpdate),
        });
      }

      // post no-preference preference
      const newDietaryPreferenceData: TDietaryPreference = await addPreference(slugToUpdate);

      return dietaryPreferenceListData
        .filter(({ dietary_preference: { exposure } }) => exposure !== exposureToUpdate)
        .concat(newDietaryPreferenceData);
    }

    if (isPreferenceToUpdateInList) {
      // delete the preference
      await removePreference(slugToUpdate);

      return dietaryPreferenceListData.filter(({ dietary_preference: { slug } }) => slug !== slugToUpdate);
    }

    const noPreferenceSlugInList = dietaryPreferenceListData
      .filter(({ dietary_preference: { exposure } }) => exposure === exposureToUpdate)
      .find(({ dietary_preference: { slug } }) => isNoPreferenceSlug(slug))?.dietary_preference?.slug;

    if (noPreferenceSlugInList) {
      // delete no-preference preference
      await removePreference(noPreferenceSlugInList);
    }

    // post a new preference
    const newDietaryPreferenceData: TDietaryPreference = await addPreference(slugToUpdate);

    return dietaryPreferenceListData
      .filter(({ dietary_preference: { slug } }) => slug !== noPreferenceSlugInList)
      .concat(newDietaryPreferenceData);
  } catch (error) {
    return Promise.reject(parseError(error));
  }
});

export const actionUpdateMultipleDietaryPreferenceListData = createTypedAsyncThunk<
  TDietaryPreference[],
  IUpdateMultipleDietaryPreferenceParams
>('actionUpdateMultipleDietaryPreferenceListData', async ({ slugsToUpdate, exposureToUpdate }, { getState }) => {
  try {
    const dietaryPreferenceListData = selectDietaryPreferenceListData(getState());

    const updatePreferences = async (slugs: string[]) => {
      const requestBody = slugs.map(slug => ({ [ApiQueries.dietaryPreference]: slug }));
      return await request({
        body: requestBody,
        method: REQUEST_METHODS.POST,
        url: dietaryPreferenceMultipleUpdateApiUrlCreator(),
      });
    };

    const shouldDeleteAllPreferencesOfExposure = dietaryPreferenceListData.some(
      ({ dietary_preference: { exposure } }) => exposure === exposureToUpdate,
    );

    if (shouldDeleteAllPreferencesOfExposure) {
      await request({
        method: REQUEST_METHODS.DELETE,
        url: dietaryPreferenceMultipleRemoveApiUrlCreator(exposureToUpdate),
      });
    }

    const newDietaryPreferenceData: TDietaryPreference = await updatePreferences(slugsToUpdate);

    return dietaryPreferenceListData
      .filter(({ dietary_preference }) => dietary_preference.exposure !== exposureToUpdate)
      .concat(newDietaryPreferenceData);
  } catch (error) {
    return Promise.reject(parseError(error));
  }
});

const dietaryPreferenceSlice = createSlice({
  extraReducers: builder => {
    builder
      .addCase(actionGetDietaryPreferenceOptionsData.pending, state => {
        state.options.error = null;
        state.options.isLoading = true;
      })
      .addCase(actionGetDietaryPreferenceOptionsData.fulfilled, (state, action) => {
        state.options.data = action.payload;
        state.options.isLoading = false;
      })
      .addCase(actionGetDietaryPreferenceOptionsData.rejected, (state, action) => {
        state.options.error = action.error;
        state.options.isLoading = false;
      })

      .addMatcher(
        isAnyOf(
          actionGetDietaryPreferenceListData.pending,
          actionUpdateDietaryPreferenceListData.pending,
          actionUpdateMultipleDietaryPreferenceListData.pending,
        ),
        state => {
          state.list.error = null;
          state.list.isLoading = true;
        },
      )
      .addMatcher(
        isAnyOf(
          actionGetDietaryPreferenceListData.fulfilled,
          actionUpdateDietaryPreferenceListData.fulfilled,
          actionUpdateMultipleDietaryPreferenceListData.fulfilled,
        ),
        (state, action) => {
          state.list.data = action.payload;
          state.list.isLoading = false;
        },
      )
      .addMatcher(
        isAnyOf(
          actionGetDietaryPreferenceListData.rejected,
          actionUpdateDietaryPreferenceListData.rejected,
          actionUpdateMultipleDietaryPreferenceListData.rejected,
        ),
        (state, action) => {
          state.list.error = action.error;
          state.list.isLoading = false;
        },
      );
  },
  initialState,
  name: 'dietaryPreference',
  reducers: {
    actionResetDietaryPreferenceListData: state => {
      state.list.data = [];
    },
  },
});

export default dietaryPreferenceSlice.reducer;
export const { actionResetDietaryPreferenceListData } = dietaryPreferenceSlice.actions;
